import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

const PACE_TONE = {
  on_track: "bg-emerald-100 text-emerald-800 border-emerald-200",
  ahead:    "bg-sky-100 text-sky-800 border-sky-200",
  behind:   "bg-rose-100 text-rose-800 border-rose-200",
  unknown:  "bg-slate-100 text-slate-700 border-slate-200",
};

const PACE_LABEL = {
  on_track: "On track",
  ahead: "Ahead",
  behind: "Behind",
  unknown: "—",
};

function toDdMmYyyy(iso) {
  if (!iso) return "—";
  const [y, m, d] = String(iso).split("-");
  if (!y || !m || !d) return iso;
  return `${d}.${m}.${y}`;
}

export function CyclePulseHeader({ cycle, stats, sla, dense = false }) {
  if (!cycle) {
    return (
      <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/50 p-6 text-center text-sm text-slate-500" data-testid="cycle-pulse-panel">
        No cycle activity yet.
      </div>
    );
  }
  const pct = Math.max(0, Math.min(100, Math.round(cycle.percent_complete || 0)));
  const paceTone = PACE_TONE[cycle.pace] || PACE_TONE.unknown;
  const ringDeg = Math.round((pct / 100) * 360);
  const ringColor = cycle.pace === "behind" ? "hsl(0 74% 50%)"
                   : cycle.pace === "ahead" ? "hsl(205 90% 45%)"
                   : cycle.percent_complete >= 100 ? "hsl(158 64% 32%)"
                   : "hsl(38 92% 50%)";
  return (
    <div className="flex flex-col gap-3 rounded-xl border bg-white p-4 shadow-sm md:flex-row md:items-center md:gap-6" data-testid="cycle-pulse-panel">
      {/* Progress ring */}
      <div className="flex shrink-0 items-center justify-center" data-testid="cycle-progress-ring">
        <div
          className="relative flex items-center justify-center rounded-full"
          style={{
            width: dense ? 72 : 96,
            height: dense ? 72 : 96,
            background: `conic-gradient(${ringColor} ${ringDeg}deg, hsl(214 20% 90%) ${ringDeg}deg)`,
          }}
        >
          <div className="absolute inset-1.5 flex flex-col items-center justify-center rounded-full bg-white text-center">
            <div className={cn(dense ? "text-base" : "text-xl", "font-display font-semibold tabular-nums")}>{pct}%</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">cycle</div>
          </div>
        </div>
      </div>
      {/* KPIs */}
      <div className="grid flex-1 grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi label="Cycle" value={`#${cycle.cycle_number}`} sub={`Started ${toDdMmYyyy(cycle.start_date)}`} testid="cycle-number" />
        <Kpi label="Day" value={`${cycle.duration_days} / ${cycle.sla_target_days || sla || "—"}`} sub="SLA target days" testid="cycle-day" />
        <Kpi label="Assets done" value={`${cycle.completed_assets} / ${cycle.completed_assets + cycle.in_progress_assets + cycle.pending_assets}`} sub={`${cycle.in_progress_assets} in progress · ${cycle.pending_assets} pending`} testid="cycle-done" />
        <Kpi label="Forecast" value={toDdMmYyyy(cycle.forecast_date)} sub={`Pace`} testid="cycle-forecast" trailing={
          <Badge variant="outline" className={cn("mt-1 border", paceTone)} data-testid="cycle-pace-badge">{PACE_LABEL[cycle.pace] || "—"}</Badge>
        } />
      </div>
      {/* History compact */}
      {stats && (stats.avg_duration_days != null) ? (
        <div className="flex shrink-0 flex-col items-end gap-0.5 border-l border-slate-100 pl-4 text-right">
          <div className="text-[11px] uppercase tracking-wider text-slate-500">Past cycles</div>
          <div className="text-sm"><span className="font-mono font-semibold" data-testid="cycle-avg-days">{stats.avg_duration_days}</span> <span className="text-slate-500">avg days</span></div>
          <div className="text-xs text-slate-500">best <span className="font-mono text-emerald-700">{stats.best_duration_days}</span> · worst <span className="font-mono text-rose-700">{stats.worst_duration_days}</span></div>
        </div>
      ) : null}
    </div>
  );
}

function Kpi({ label, value, sub, trailing, testid }) {
  return (
    <div className="flex flex-col gap-0.5" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-base font-semibold tabular-nums leading-tight md:text-lg">{value}</div>
      {sub ? <div className="text-[11px] text-slate-500">{sub}</div> : null}
      {trailing || null}
    </div>
  );
}
